import axios from 'axios'
import { 
  User, 
  LoginResponse, 
  RegisterResponse, 
  IdentityVerificationResponse 
} from '../../types/auth'

// Determine API base URL at runtime
// In production (Cloudflare Pages), always use relative /api path (handled by Cloudflare Functions)
// In development, use VITE_API_BASE_URL if set, otherwise default to /api (proxy)
const getApiBaseUrl = (): string => {
  // Always check runtime hostname first - this is the source of truth
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname
    const protocol = window.location.protocol
    
    // If we're on HTTPS (production), ALWAYS use /api (ignore any env vars)
    // This prevents CORS issues when production tries to access localhost
    if (protocol === 'https:') {
      return '/api'
    }
    
    // If we're on a production domain, ALWAYS use /api (ignore any env vars)
    if (hostname === 'open1031.pages.dev' || 
        hostname.endsWith('.pages.dev') ||
        hostname === 'open1031.ai' ||
        hostname.endsWith('.open1031.ai') ||
        hostname.includes('cloudflarepages.dev')) {
      return '/api'
    }
    
    // NEVER use localhost URLs in production - they cause CORS issues
    if (import.meta.env.PROD) {
      return '/api'
    }
    
    // If we're on localhost in dev, use /api (Vite proxy)
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return '/api'
    }
  }
  
  // In development only, check if VITE_API_BASE_URL is set and not localhost
  // But only if we're not in production mode
  if (!import.meta.env.PROD) {
    const envUrl = import.meta.env.VITE_API_BASE_URL
    // NEVER use localhost URLs - they cause CORS issues
    if (envUrl && 
        !envUrl.includes('localhost') && 
        !envUrl.includes('127.0.0.1') &&
        !envUrl.startsWith('http://localhost') &&
        !envUrl.startsWith('https://localhost')) {
      return envUrl
    }
  }
  
  // Default to relative /api (will use Vite proxy in dev, Cloudflare Functions in prod)
  return '/api'
}

const api = axios.create({
  baseURL: '/api', // Default, will be overridden by interceptor
  headers: {
    'Content-Type': 'application/json',
  },
})

// Ensure baseURL is always correct on each request
api.interceptors.request.use((config) => {
  // Recompute baseURL on each request to ensure it's always correct
  const baseURL = getApiBaseUrl()
  
  // Safety check: NEVER allow localhost URLs if we're on HTTPS (production)
  if (typeof window !== 'undefined' && window.location.protocol === 'https:' && baseURL.includes('localhost')) {
    console.warn('Blocked localhost URL in production, using /api instead')
    config.baseURL = '/api'
  } else {
    config.baseURL = baseURL
  }
  
  const token = localStorage.getItem('auth_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

interface RegisterData {
  email: string
  password: string
  firstName: string
  lastName: string
  phone: string
  dateOfBirth: string
  ssn: string
  address: {
    street: string
    city: string
    state: string
    zipCode: string
  }
  propertyAddress?: {
    street?: string
    city?: string
    state?: string
    zipCode?: string
  }
}

interface IdentityVerificationData {
  documentType: 'drivers_license' | 'passport' | 'state_id'
  documentFront: File
  documentBack?: File
  selfie: File
}

export const authApi = {
  async login(email: string, password: string): Promise<LoginResponse> {
    const response = await api.post('/auth/login', { email, password })
    return response.data
  },

  async register(userData: RegisterData): Promise<RegisterResponse> {
    const response = await api.post('/auth/register', userData)
    return response.data
  },

  async getCurrentUser(): Promise<User> {
    const response = await api.get('/auth/me')
    return response.data
  },

  async verifyIdentity(verificationData: IdentityVerificationData): Promise<IdentityVerificationResponse> {
    const formData = new FormData()
    formData.append('documentType', verificationData.documentType)
    formData.append('documentFront', verificationData.documentFront)
    if (verificationData.documentBack) {
      formData.append('documentBack', verificationData.documentBack)
    }
    formData.append('selfie', verificationData.selfie)

    const response = await api.post('/auth/verify-identity', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  },

  async updateProfile(profileData: Partial<User>): Promise<User> {
    const response = await api.patch('/auth/profile', profileData)
    return response.data
  },

  async forgotPassword(email: string): Promise<void> {
    await api.post('/auth/forgot-password', { email })
  },

  async resetPassword(token: string, password: string): Promise<void> {
    await api.post('/auth/reset-password', { token, password })
  },

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await api.post('/auth/change-password', { currentPassword, newPassword })
  },

  async resetDemo(): Promise<{ success: boolean; user: User }> {
    const response = await api.post('/auth/reset-demo')
    return response.data
  },
}
